import orderService from "@/services/orderService";
import { formatCurrency } from "@/lib/format";
import { getCompanyName } from "@/lib/company";

/** Pedido em andamento há mais que isso ganha destaque de atenção */
export const LATE_AFTER_MINUTES = 30;

/** Fluxo do pedido (barra de progresso do card) */
export const FLOW = ["Aguardando", "Preparando", "Liberado", "Entregue"];

/** Status que ainda pedem ação da loja */
export const OPEN_STATUSES = ["Aguardando", "Preparando", "Liberado"];

/** Próximo passo de cada status (botão principal do card) */
export const NEXT_STEP = {
  Aguardando: { status: "Preparando", label: "Aceitar" },
  Preparando: { status: "Liberado", label: "Liberar" },
  Liberado: { status: "Entregue", label: "Entregue" },
  Entregue: { status: "Finalizado", label: "Concluir" },
};

export const isDelivery = (order) =>
  String(order?.delivery_type || "").toLowerCase() === "delivery";

/**
 * Nome exibido do status. "Liberado" vira "Em Rota" no delivery e "Pronto"
 * na retirada (mesmo status na API).
 */
export const getStatusLabel = (order) => {
  switch (order?.status) {
    case "Aguardando":
      return "Novo";
    case "Preparando":
      return "Em Preparo";
    case "Liberado":
      return isDelivery(order) ? "Em Rota" : "Pronto";
    default:
      return order?.status || "—";
  }
};

/** Etapa exibida na barra de progresso */
export const getStageLabel = (order) => {
  switch (order?.status) {
    case "Aguardando":
      return "Aguardando aceite";
    case "Preparando":
      return "Em preparo";
    case "Liberado":
      return isDelivery(order) ? "Saiu para entrega" : "Pronto para retirada";
    case "Entregue":
      return "Entregue ao cliente";
    default:
      return order?.status || "";
  }
};

/** 0–1 conforme a posição do status no fluxo (null = fora do fluxo) */
export const getProgress = (order) => {
  const index = FLOW.indexOf(order?.status);
  return index < 0 ? null : (index + 1) / FLOW.length;
};

/** Abas de status (chips): `match` decide se o pedido entra na aba */
export const STATUS_TABS = [
  { id: "all", label: "Todos", match: () => true },
  { id: "new", label: "Novos", match: (o) => o.status === "Aguardando" },
  { id: "preparing", label: "Em Preparo", match: (o) => o.status === "Preparando" },
  {
    id: "ready",
    label: "Prontos",
    match: (o) => o.status === "Liberado" && !isDelivery(o),
  },
  {
    id: "route",
    label: "Em Rota",
    match: (o) => o.status === "Liberado" && isDelivery(o),
  },
  { id: "delivered", label: "Entregues", match: (o) => o.status === "Entregue" },
  { id: "done", label: "Finalizados", match: (o) => o.status === "Finalizado" },
  { id: "cancelled", label: "Cancelados", match: (o) => o.status === "Cancelado" },
];

/** Aba inicial a partir do status vindo do Header (ex.: "Aguardando") */
export const getTabForStatus = (status) => {
  const name = orderService.getStatusName(status);
  const tab = {
    Aguardando: "new",
    Preparando: "preparing",
    Entregue: "delivered",
    Finalizado: "done",
    Cancelado: "cancelled",
  }[name];
  return tab || "all";
};

export const SORTS = {
  priority: "Prioridade",
  recent: "Mais recentes",
  oldest: "Mais antigos",
};

const orderTime = (order) =>
  orderService.parseDateTime(order?.date_time)?.getTime() || 0;

/**
 * Prioridade: em andamento primeiro (na ordem do fluxo, o mais antigo antes);
 * depois os encerrados, do mais recente ao mais antigo.
 */
export const sortOrders = (orders, sortBy) => {
  const list = [...orders];
  if (sortBy === "recent") return list.sort((a, b) => orderTime(b) - orderTime(a));
  if (sortBy === "oldest") return list.sort((a, b) => orderTime(a) - orderTime(b));
  const rank = (o) => {
    const index = FLOW.indexOf(o.status);
    return index < 0 ? FLOW.length : index;
  };
  return list.sort((a, b) => {
    const byRank = rank(a) - rank(b);
    if (byRank !== 0) return byRank;
    return rank(a) < FLOW.length
      ? orderTime(a) - orderTime(b)
      : orderTime(b) - orderTime(a);
  });
};

/** Minúsculas e sem acento, para a busca */
export const normalizeText = (value) =>
  String(value ?? "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase();

export const DEFAULT_FILTERS = {
  search: "",
  payment: "all", // all | paid | unpaid
  delivery: "all", // all | delivery | pickup
  period: "all", // all | today | custom
  startDate: "", // yyyy-MM-dd (period = custom)
  endDate: "",
  sortBy: "priority",
};

const dayBoundary = (value, endOfDay) => {
  if (!value) return null;
  const [year, month, day] = value.split("-").map(Number);
  if ([year, month, day].some(Number.isNaN)) return null;
  return endOfDay
    ? new Date(year, month - 1, day, 23, 59, 59, 999)
    : new Date(year, month - 1, day);
};

/** Aplica busca, pagamento, entrega e período (sem a aba de status) */
export const applyFilters = (orders, filters, now = new Date()) => {
  const term = normalizeText(filters.search).trim();
  let start = null;
  let end = null;
  if (filters.period === "today") {
    start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  } else if (filters.period === "custom") {
    start = dayBoundary(filters.startDate, false);
    end = dayBoundary(filters.endDate, true);
  }

  return orders.filter((order) => {
    if (
      term &&
      !normalizeText(order.code).includes(term) &&
      !normalizeText(order.client?.name).includes(term)
    ) {
      return false;
    }
    if (filters.payment === "paid" && order.payed !== true) return false;
    if (filters.payment === "unpaid" && order.payed === true) return false;
    if (filters.delivery === "delivery" && !isDelivery(order)) return false;
    if (filters.delivery === "pickup" && isDelivery(order)) return false;
    if (start || end) {
      const date = orderService.parseDateTime(order.date_time);
      if (!date) return false;
      if (start && date < start) return false;
      if (end && date > end) return false;
    }
    return true;
  });
};

/** Quantos filtros (além da ordenação) estão ativos */
export const countActiveFilters = (filters) =>
  [
    filters.search.trim(),
    filters.payment !== "all",
    filters.delivery !== "all",
    filters.period !== "all",
  ].filter(Boolean).length;

/** Média, em minutos, do tempo em aberto dos pedidos em andamento */
export const getAverageOpenMinutes = (orders, now = new Date()) => {
  const ages = orders
    .filter((order) => OPEN_STATUSES.includes(order.status))
    .map((order) => orderService.parseDateTime(order.date_time))
    .filter(Boolean)
    .map((date) => Math.max(0, (now - date) / 60000));
  if (!ages.length) return null;
  return Math.round(ages.reduce((sum, age) => sum + age, 0) / ages.length);
};

/** Minutos desde a criação do pedido (null sem data válida) */
export const getElapsedMinutes = (order, now = new Date()) => {
  const date = orderService.parseDateTime(order?.date_time);
  return date ? Math.max(0, Math.floor((now - date) / 60000)) : null;
};

/** Quantidade total de itens (soma das quantidades) */
export const countItems = (order) =>
  (order?.products || []).reduce(
    (sum, item) => sum + (Number(item.quantity) || 1),
    0,
  );

/** "1x Pão na Chapa, 1x Suco Verde" */
export const summarizeItems = (order) =>
  (order?.products || [])
    .map((item) => `${item.quantity || 1}x ${item.product?.name || "Produto"}`)
    .join(", ");

/** Subtotal dos itens, desconto do cupom e frete (total = subtotal − desconto + frete) */
export const getOrderAmounts = (order) => {
  const subtotal = (order?.products || []).reduce(
    (sum, item) => sum + (parseFloat(item.total_value) || 0),
    0,
  );
  const discountPercentage = parseFloat(order?.discount_percentage) || 0;
  return {
    subtotal,
    discountPercentage,
    discount: subtotal * (discountPercentage / 100),
    freight: parseFloat(order?.freight_value) || 0,
    total: orderService.calculateTotal(order),
  };
};

/** Telefone do cliente só com dígitos (com DDI 55) ou "" */
export const getWhatsAppNumber = (order) => {
  const digits = String(order?.client?.contact || order?.client?.phone || "").replace(
    /\D/g,
    "",
  );
  if (!digits) return "";
  return digits.startsWith("55") && digits.length > 11 ? digits : `55${digits}`;
};

export const buildWhatsAppUrl = (order) => {
  const phone = getWhatsAppNumber(order);
  if (!phone) return "";
  const companyName = getCompanyName(order);
  const lines = [
    `Olá ${order.client?.name || "cliente"}!${companyName ? ` Aqui é a equipe ${companyName}.` : ""}`,
    `Estamos entrando em contato sobre o pedido #${order.code} (${getStatusLabel(order)}).`,
    `Valor do pedido: ${formatCurrency(orderService.calculateTotal(order))}`,
    "Qualquer dúvida é só responder!",
  ];
  return `https://wa.me/${phone}?text=${encodeURIComponent(lines.join("\n"))}`;
};

/** Endereço de entrega (AddressUSDTO: street, number, neighborhood, zipCode...) */
export const getDeliveryAddress = (order) => {
  const parse = (value) => {
    if (!value) return null;
    if (typeof value === "object") return value;
    try {
      return JSON.parse(value);
    } catch (error) {
      return { description: String(value) };
    }
  };
  const sources = [
    parse(order?.address),
    parse(order?.delivery_address),
    parse(order?.client?.address),
  ].filter(Boolean);
  const pick = (...keys) => {
    for (const source of sources) {
      for (const key of keys) {
        const value = source?.[key];
        if (typeof value === "string" && value.trim()) return value.trim();
      }
    }
    return "";
  };

  const street = pick("street", "logradouro", "rua", "description");
  const number = pick("number", "numero");
  const complement = pick("complement", "complemento");
  const neighborhood = pick("neighborhood", "bairro");
  const city = pick("city", "cidade", "locality");
  const uf = pick("uf", "state", "estado");
  const zipCode = pick("zipCode", "cep", "zip_code");
  const reference = pick("reference", "referencia");

  const lines = [
    street && `${street}${number ? `, ${number}` : ""}${complement ? ` (${complement})` : ""}`,
    [neighborhood, [city, uf].filter(Boolean).join(" - ")].filter(Boolean).join(" • "),
    [zipCode && `CEP ${zipCode}`, reference && `Ref.: ${reference}`]
      .filter(Boolean)
      .join(" • "),
  ].filter(Boolean);

  const query = [street && `${street} ${number}`.trim(), neighborhood, city, uf, zipCode]
    .filter(Boolean)
    .join(", ");

  return { lines, mapQuery: query ? encodeURIComponent(query) : "" };
};

/** Texto da comanda para impressora térmica de 58 mm (32 colunas, sem acento) */
export const buildReceiptText = (order) => {
  const width = 32;
  const strong = "=".repeat(width);
  const thin = "-".repeat(width);
  const plain = (value) =>
    String(value ?? "")
      .normalize("NFD")
      .replace(/[̀-ͯ]/g, "");
  const center = (value) =>
    " ".repeat(Math.max(0, Math.floor((width - value.length) / 2))) + value;
  const money = (value) => `R$ ${(Number(value) || 0).toFixed(2)}`;
  const wrap = (value, indent = "") => {
    const lines = [];
    let line = indent;
    plain(value)
      .split(/\s+/)
      .filter(Boolean)
      .forEach((word) => {
        if (line.trim() && (line + word).length > width) {
          lines.push(line.trimEnd());
          line = indent;
        }
        line += `${word} `;
      });
    if (line.trim()) lines.push(line.trimEnd());
    return lines;
  };

  const { subtotal, discount, discountPercentage, freight, total } =
    getOrderAmounts(order);
  // Nome da empresa em maiúsculas, quebrado em linhas de até 32 colunas
  const brand = wrap(getCompanyName(order).toUpperCase()).map(center);
  const out = [
    ...brand,
    center("Comanda de Pedido"),
    strong,
    `Pedido #${order.code}`,
    `Data ${order.date_time || "-"}`,
    `Status ${plain(getStatusLabel(order))}`,
    strong,
    "",
    "CLIENTE",
    ...wrap(order.client?.name || "Nao informado"),
    `Tel ${order.client?.contact || "-"}`,
    strong,
    "",
    "ITENS DO PEDIDO",
    thin,
  ];

  const products = order.products || [];
  if (!products.length) out.push("Nenhum produto encontrado");
  products.forEach((item, index) => {
    const quantity = Number(item.quantity) || 1;
    const itemTotal = parseFloat(item.total_value) || 0;
    out.push(
      ...wrap(`${index + 1}. ${item.product?.name || "Produto"} | Qtd ${quantity}`),
      `   Un. ${money(itemTotal / quantity)}`,
      `   Tot ${money(itemTotal)}`,
    );
    [
      ["Produtos Selecionados", item.selected_produtos],
      ["Produtos Extras", item.selectedExtraProdutos],
    ].forEach(([title, list]) => {
      if (!list?.length) return;
      out.push(`   ${title}:`);
      list.forEach((extra) => {
        out.push(`   + ${plain(extra.name)} x${extra.qty || 1}`);
        out.push(`     ${money((Number(extra.price) || 0) * (extra.qty || 1))}`);
      });
    });
    [
      ["Ingredientes", item.selected_ingredients],
      ["Ingredientes Extras", item.selectedExtraIngredients],
    ].forEach(([title, list]) => {
      if (!list?.length) return;
      out.push(`   ${title}:`);
      list.forEach((ingredient) => out.push(`   * ${plain(ingredient.name)}`));
    });
    if (item.observation) {
      out.push("   Obs:", ...wrap(item.observation, "   "));
    }
    out.push(thin);
  });

  out.push("", strong, `Subtotal ${money(subtotal)}`);
  if (discount > 0) {
    out.push(
      `Desconto ${plain(order.coupon_code || "")} ${discountPercentage.toFixed(1)}%`,
      `-${money(discount)}`,
    );
  }
  if (freight > 0) out.push(`Entrega ${money(freight)}`);
  out.push(
    strong,
    `TOTAL ${money(total)}`,
    strong,
    "",
    `Pagamento ${plain(order.pay_method || "-")}`,
    `Entrega ${plain(order.delivery_type || "-")}`,
  );
  if (order.payed) out.push("Status PAGO");
  out.push(strong, "", center("Obrigado!"), ...brand, "");

  return out.join("\n");
};

/** Copia o texto (clipboard moderno, com fallback para contexto não seguro) */
export const copyText = async (text) => {
  if (navigator.clipboard && window.isSecureContext) {
    await navigator.clipboard.writeText(text);
    return;
  }
  const textArea = document.createElement("textarea");
  textArea.value = text;
  textArea.style.position = "fixed";
  textArea.style.left = "-9999px";
  document.body.appendChild(textArea);
  textArea.select();
  const ok = document.execCommand("copy");
  textArea.remove();
  if (!ok) throw new Error("Falha ao copiar");
};
